import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getBackendBaseUrl } from "../../../utils/apiUrl";
import { joinDealRoom, leaveDealRoom, onDealUpdated } from "../../../utils/socketClient";
import { FaCar, FaShieldAlt, FaPercentage, FaCalendarAlt, FaDollarSign, FaFileContract, FaSyncAlt } from 'react-icons/fa';

const LiveDealView = () => {
const getCarImageUrl = (vehicle, selectedColorName) => {
  if (!vehicle) return "";
  let img = vehicle.imageUrl;
  if (selectedColorName && vehicle.colors && vehicle.colors.length > 0) {
    const matchedColor = vehicle.colors.find(c => c.name === selectedColorName);
    if (matchedColor && matchedColor.imageUrl) {
      img = matchedColor.imageUrl;
    }
  }
  return img || "https://images.unsplash.com/photo-1549399542-7e3f8b79c341?auto=format&fit=crop&q=80&w=400";
};

  const { token } = useParams();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchLiveDeal = async () => {
    try {
      const res = await fetch(`${getBackendBaseUrl()}/d2-deals/live/${token}`);
      const data = await res.json();
      if (data.success) {
        setDeal(data.data);
      } else {
        setError(data.message || "Unable to retrieve live deal.");
      }
    } catch (err) {
      console.error("Failed to fetch live deal", err);
      setError("Network error fetching deal.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLiveDeal();
    if (!token) return;

    joinDealRoom(token);

    onDealUpdated((data) => {
      if (data?.deal) {
        setDeal(data.deal);
      }
    });

    return () => {
      leaveDealRoom(token);
    };
  }, [token]);


  if (loading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-slate-950 text-slate-200">
        <div className="flex flex-col items-center gap-3">
          <span className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></span>
          <p className="text-slate-400 text-sm font-medium tracking-wide">Connecting to Secure Server...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-slate-950 text-slate-200 p-4">
        <div className="max-w-md text-center bg-slate-900 border border-slate-800 rounded-3xl p-8 shadow-2xl">
          <span className="text-3xl text-red-500 block mb-4">⚠️</span>
          <h2 className="text-xl font-bold text-white tracking-tight">Connection Error</h2>
          <p className="text-slate-400 text-xs mt-2 leading-relaxed">{error}</p>
        </div>
      </div>
    );
  }

  if (!deal) return null;

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white font-sans flex flex-col justify-center items-center p-4 animate-fade-in-up selection:bg-indigo-500 selection:text-white">
      {/* Glow overlays */}
      <div className="fixed -top-40 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-indigo-500/10 rounded-full blur-[120px] pointer-events-none -z-10"></div>
      
      <div className="w-full max-w-md bg-slate-900/80 backdrop-blur-md rounded-3xl border border-slate-800/80 shadow-2xl overflow-hidden relative">
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-indigo-500 to-transparent"></div>
        
        {/* Card Header */}
        <div className="bg-slate-900/40 p-6 text-center border-b border-slate-800/60 relative">
          <div className="absolute right-4 top-4 flex items-center gap-1 text-[10px] font-bold text-indigo-400 uppercase tracking-widest bg-indigo-500/10 px-2 py-1 rounded-full border border-indigo-500/20">
            <FaSyncAlt className="animate-spin text-[8px]" /> Live
          </div>
          <h1 className="text-xl font-black text-white tracking-tight">Your Live Deal</h1>
          <p className="text-slate-400 text-xs mt-1 font-medium">Updates in real-time as terms are structured</p>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Vehicle Section */}
          {deal.vehicle && deal.vehicle.year ? (
            <div className="border-b border-slate-800/50 pb-5">
              <h2 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-3">Vehicle Details</h2>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <span className="p-2.5 bg-slate-850 border border-slate-800 text-indigo-400 rounded-xl">
                    <FaCar size={14} />
                  </span>
                  <div>
                    <span className="font-extrabold text-white text-sm block">
                      {deal.vehicle.year} {deal.vehicle.make}
                    </span>
                    <span className="text-slate-400 text-xs block font-semibold">{deal.vehicle.model}</span>
                  </div>
                </div>
                <span className="text-base font-extrabold text-white">${(deal.vehicle.salePrice || 0).toLocaleString()}</span>
              </div>
            </div>
          ) : (
            <div className="text-center text-slate-400 italic text-xs py-4">No vehicle selected yet</div>
          )}

          
          {/* Calculation Breakdown */}
          {deal.calculation && (
            <div className="space-y-4">
              <h2 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider border-b border-slate-800/50 pb-2">Deal Structure</h2>
              <div className="space-y-2.5 text-xs text-slate-300">
                <div className="flex justify-between">
                  <span className="text-slate-400">Sale Price</span>
                  <span className="font-bold text-white">${deal?.vehicle?.salePrice?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Accessories</span>
                  <span className="font-bold text-white">+${(deal?.vehicle?.accessories || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Taxes & Fees</span>
                  <span className="font-bold text-white">+${((deal?.calculation?.salesTax || 0) + (deal?.vehicle?.docFee || 800) + (deal?.vehicle?.registration || 772)).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-white border-t border-slate-800/60 pt-1.5 font-bold">
                  <span>Total Amount Due</span>
                  <span>${(deal.calculation.totalAmountDue || 0).toLocaleString()}</span>
                </div>
                {deal.calculation.tradeEquity !== 0 && (
                  <div className="flex justify-between text-indigo-400">
                    <span>Trade Equity</span>
                    <span>${(deal.calculation.tradeEquity).toLocaleString()}</span>
                  </div>
                )}
                {deal.finance?.downPayment > 0 && (
                  <div className="flex justify-between text-emerald-400">
                    <span>Down Payment</span>
                    <span>-${(deal.finance.downPayment).toLocaleString()}</span>
                  </div>
                )}
                {deal.finance?.rebate > 0 && (
                  <div className="flex justify-between text-emerald-400">
                    <span>Manufacturer Rebates</span>
                    <span>-${(deal.finance.rebate).toLocaleString()}</span>
                  </div>
                )}
                
                {deal.fAndI?.products && deal.fAndI.products.length > 0 && (
                  <div className="space-y-1.5 pt-1.5 border-t border-slate-850">
                    <div className="text-[9px] font-extrabold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                      <FaShieldAlt size={10} className="text-emerald-400" /> Selected Coverages
                    </div>
                    {deal.fAndI.products.map(prod => (
                      <div key={prod._id} className="flex justify-between text-emerald-400 text-xs pl-3 border-l border-emerald-500/20 py-0.5">
                        <span>{prod.name}</span>
                        <span className="font-bold">+${(prod.price || 0).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-slate-850 flex justify-between items-center">
                <span className="text-slate-450 text-xs font-bold uppercase flex items-center gap-1.5">
                  <FaDollarSign size={10} className="text-slate-500" /> Total Amount Financed
                </span>
                <span className="text-base font-extrabold text-white">${(deal.calculation.totalAmountFinanced || deal.calculation.baseAmountFinanced || 0).toLocaleString()}</span>
              </div>
            </div>
          )}

          {/* Estimated Payment */}
          {deal.calculation?.payment > 0 && (
            <div className="bg-slate-850 border border-slate-800 rounded-2xl p-5 text-center shadow-md relative overflow-hidden">
              <div className="absolute right-0 bottom-0 w-24 h-24 bg-indigo-500/5 rounded-full blur-xl"></div>
              <p className="text-[10px] text-indigo-400 font-extrabold uppercase tracking-wider mb-1">Estimated Monthly Payment</p>
              <p className="text-4xl font-black text-white tracking-tight">
                ${deal.calculation.payment.toLocaleString()}
              </p>
              <p className="text-[10px] text-slate-400 font-bold mt-2 flex items-center justify-center gap-3">
                <span className="flex items-center gap-1"><FaCalendarAlt size={10} className="text-slate-500" /> {deal.lender?.term || 60} Months</span>
                <span className="w-1.5 h-1.5 bg-slate-700 rounded-full"></span>
                <span className="flex items-center gap-1"><FaPercentage size={10} className="text-slate-500" /> {deal.lender?.apr || 8}% APR</span>
              </p>
            </div>
          )}
        </div>
      </div>
      
      <p className="text-center text-[10px] text-slate-500 mt-6 leading-relaxed flex items-center gap-1">
        🔒 Real-time synchronization active. Structured via Salesense AI.
      </p>
    </div>
  );
};

export default LiveDealView;
